import { useSyncHorizontalScroll } from '../../hooks/useSyncHorizontalScroll';
import { useCalculateFetchedItems } from '../../hooks/useCalculateFetchItems';
import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useScrollFetchData } from '../../hooks/useScrollFetchData';
import CustomHeaderCell from '../../utils/customHeaderCell';
import ContractsListViewData from './contractsListViewData';
import { useNavigate } from 'react-router-dom';
import { PersistedFixedSizeList as FixedSizeList } from "../../hooks/listNavigation";
import { Box } from '@mui/material';
import { debounce } from 'lodash';

const defaultColumnWidth = {
    title: 250,
    contract_number: 250,
    company: 250,
    machines: 250,
    date: 250,
    auto_renew: 250,
    visit_frequency: 250,
};

const ContractsListView = React.memo(({ contractFilters, setContractFilters, companies, sortBy, setSortBy, scrollRef, contracts, setOffset, filteredContractsCount, setContractsFetched }) => {

    const navigate = useNavigate();

    const [contractColumnWidth, setContractColumnWidth] = useState(() => {
        const storedWidths = JSON.parse(localStorage.getItem('contractColumnWidth')) || {};
        return { ...defaultColumnWidth, ...storedWidths }; // Merge defaults with stored widths
    });

    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    // Update window height on resize
    useEffect(() => {
        const handleResize = debounce(() => {
            setWindowHeight(window.innerHeight);
        }, 300); // 300ms debounce delay

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            handleResize.cancel(); // Cancel any pending debounced calls
        };
    }, []);

    const handleSort = (field) => {
        setSortBy((prevSortBy) => {
            const nextSortOrder = prevSortBy[field] === null ? 'asc'
                : prevSortBy[field] === 'asc' ? 'desc'
                    : null;

            return Object.fromEntries(
                Object.keys(prevSortBy)?.map((key) => [key, key === field ? nextSortOrder : null])
            );
        });

    };

    const auto_renewOptions = [{ id: 'true', name: 'Automatique' }, { id: 'false', name: 'Non automatique' }];

    const tableHeadRef = useRef(null);

    // synchronize horizontal scroll between table head and list
    useSyncHorizontalScroll(tableHeadRef, scrollRef);

    // Calculate fetched items on mount
    useCalculateFetchedItems(filteredContractsCount, setContractsFetched, 70, 280);

    // Handle scroll and fetch more items
    const handleScrollList = useScrollFetchData(setOffset, setContractsFetched, filteredContractsCount, 70, 280);

    const handleContractClick = useCallback((id) => {
        navigate(`contract_details/${id}`);
    }, [navigate]);

    const handleColumnWidthChange = (value, columnKey) => {
        setContractColumnWidth({ ...contractColumnWidth, [columnKey]: value })
    }

    useEffect(() => {
        localStorage.setItem('contractColumnWidth', JSON.stringify(contractColumnWidth));
    }, [contractColumnWidth]);

    const Row = ({ index, style, data }) => {
        const { contracts, handleContractClick } = data;
        const contract = index === 0 ? contracts[0] : contracts[index];
        return (
            <Box key={index} style={{ ...style, width: 'fit-content' }}>
                <ContractsListViewData
                    contract={contract}
                    columnWidth={contractColumnWidth}
                    handleContractClick={handleContractClick}
                />
            </Box>
        );
    };

    return (
        <Box className='table' sx={{ width: '98% !important', margin: '0 auto' }}>
            <Box className='tableHead' ref={tableHeadRef}>
                <CustomHeaderCell
                    backgroundColor='#F6F6F6'
                    label="Titre"
                    columnKey='title'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFixed={true}
                    columnWidth={contractColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    backgroundColor='#F6F6F6'
                    label='Entreprise associé'
                    columnKey='company'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={true}
                    value={contractFilters.company}
                    onChange={(e) => setContractFilters({ ...contractFilters, company: e.target.value })}
                    options={companies}
                    isFixed={false}
                    columnWidth={contractColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    backgroundColor='#F6F6F6'
                    label='Machines concernées'
                    columnKey='machines'
                    isFixed={false}
                    columnWidth={contractColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    backgroundColor='#F6F6F6'
                    label='Date'
                    columnKey='date'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={true}
                    value={contractFilters.date}
                    onChange={(e) => { setContractFilters({ ...contractFilters, date: e }) }}
                    options='date'
                    isFixed={false}
                    columnWidth={contractColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    backgroundColor='#F6F6F6'
                    label='Renouvellement automatique'
                    columnKey='auto_renew'
                    isFilter={true}
                    value={contractFilters.auto_renew}
                    onChange={(e) => { setContractFilters({ ...contractFilters, auto_renew: e.target.value }) }}
                    options={auto_renewOptions}
                    isFixed={false}
                    columnWidth={contractColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    backgroundColor='#F6F6F6'
                    label='Suivi des visites'
                    columnKey='visit_frequency'
                    isFixed={false}
                    columnWidth={contractColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    backgroundColor='#F6F6F6'
                    label='Numéro du contrat'
                    columnKey='contract_number'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFixed={false}
                    columnWidth={contractColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    backgroundColor='#F6F6F6' width="50px"
                />
            </Box>
            <Box className='tableBody'>
                <FixedSizeList
                    ref={scrollRef}
                    height={Math.min(
                        filteredContractsCount * 70 + 15,
                        windowHeight - 285
                    )}
                    width='100%'
                    itemCount={filteredContractsCount}
                    itemSize={70}
                    itemData={{ contracts, handleContractClick }}
                    onScroll={handleScrollList}
                >
                    {Row}
                </FixedSizeList>
            </Box>
        </Box>

    );
});

export default ContractsListView;